import { useState, useEffect } from 'react'
import { Archive, Trash2, Eye, Download, Search, Calendar, AlertCircle, CheckCircle, Shield, ArrowRight, BarChart3, Filter } from 'lucide-react'

export default function AuditHistoryPage() {
  const [audits, setAudits] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedAudit, setSelectedAudit] = useState(null)
  const [filterStatus, setFilterStatus] = useState('all')
  const [error, setError] = useState(null)

  // Hardcoded company ID for demonstration
  const COMPANY_ID = '00000000-0000-0000-0000-000000000001'

  useEffect(() => {
    fetchAuditHistory()
  }, [])

  const fetchAuditHistory = async () => {
    setLoading(true)
    try {
      const response = await fetch(`http://localhost:8000/api/auditor/history?company_id=${COMPANY_ID}`)
      if (!response.ok) throw new Error('Failed to fetch audit history')
      const data = await response.json()
      setAudits(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchAuditDetails = async (auditId) => {
    try {
      const response = await fetch(`http://localhost:8000/api/auditor/details/${auditId}`)
      if (!response.ok) throw new Error('Failed to fetch audit details')
      const data = await response.json()
      setSelectedAudit(data)
    } catch (err) {
      console.error(err)
    }
  }

  const deleteAudit = async (auditId) => {
    // In a professional app, we'd call a DELETE API
    // For now, let's just update the local state to simulate it
    setAudits(audits.filter(a => a.id !== auditId))
    if (selectedAudit && selectedAudit.record.id === auditId) {
      setSelectedAudit(null)
    }
  }

  const filteredAudits = audits.filter((audit) => {
    const matchesSearch =
      audit.audit_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      audit.id.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesFilter = filterStatus === 'all' || audit.status.toLowerCase() === filterStatus.toLowerCase()

    return matchesSearch && matchesFilter
  })

  if (selectedAudit) {
    const { record, findings } = selectedAudit
    return (
      <div className="space-y-6 animate-in fade-in duration-300">
        <button
          onClick={() => setSelectedAudit(null)}
          className="px-4 py-2 text-blue-400 hover:text-white flex items-center space-x-2 font-bold text-sm uppercase tracking-widest transition-colors"
        >
          <ArrowRight className="rotate-180" size={16} />
          <span>Back to History</span>
        </button>

        {/* Audit Detail View (Similar to LiveAudit results but static) */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-blue-500/20 p-8 shadow-2xl">
           <div className="flex justify-between items-start mb-8">
              <div>
                <h1 className="text-3xl font-black text-white uppercase tracking-tight">{record.audit_type} Assessment</h1>
                <p className="text-blue-300/60 font-mono text-xs mt-1">Ref: {record.id}</p>
              </div>
              <div className={`px-6 py-2 rounded-full font-black text-sm uppercase tracking-widest border ${
                record.status === 'passed' ? 'bg-green-500/10 text-green-400 border-green-500/30' : 
                record.status === 'failed' ? 'bg-red-500/10 text-red-400 border-red-500/30' : 
                'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'
              }`}>
                {record.status}
              </div>
           </div>

           <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
              <div className="bg-slate-900/50 p-4 rounded-xl border border-blue-500/5">
                <p className="text-[10px] font-black text-blue-300/40 uppercase mb-1">Execution Date</p>
                <p className="text-white font-bold">{new Date(record.started_at).toLocaleDateString()}</p>
                <p className="text-blue-300/60 text-xs">{new Date(record.started_at).toLocaleTimeString()}</p>
              </div>
              <div className="bg-slate-900/50 p-4 rounded-xl border border-blue-500/5">
                <p className="text-[10px] font-black text-blue-300/40 uppercase mb-1">Critical Findings</p>
                <p className="text-red-400 text-2xl font-black">{record.critical_issues}</p>
              </div>
              <div className="bg-slate-900/50 p-4 rounded-xl border border-blue-500/5">
                <p className="text-[10px] font-black text-blue-300/40 uppercase mb-1">High Risk</p>
                <p className="text-orange-400 text-2xl font-black">{record.high_risk_issues}</p>
              </div>
              <div className="bg-slate-900/50 p-4 rounded-xl border border-blue-500/5">
                <p className="text-[10px] font-black text-blue-300/40 uppercase mb-1">Success Rate</p>
                <p className="text-green-400 text-2xl font-black">
                  {record.total_findings > 0 ? Math.round(((record.total_findings - (record.critical_issues + record.high_risk_issues)) / record.total_findings) * 100) : 100}%
                </p>
              </div>
           </div>

           <h2 className="text-xl font-black text-white mb-4 uppercase tracking-wider flex items-center space-x-2">
              <Shield size={20} className="text-blue-400" />
              <span>Assessment Findings</span>
           </h2>

           <div className="space-y-4">
              {findings.map((item, idx) => (
                <div key={idx} className="bg-slate-900/30 rounded-xl border border-blue-500/10 p-5">
                   <div className="flex justify-between items-start gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-3">
                           <span className={`text-[9px] font-black px-1.5 py-0.5 rounded border uppercase ${
                             item.severity === 'critical' ? 'text-red-400 border-red-400/30 bg-red-400/10' :
                             item.severity === 'high' ? 'text-orange-400 border-orange-400/30 bg-orange-400/10' :
                             'text-blue-400 border-blue-400/30 bg-blue-400/10'
                           }`}>
                             {item.severity}
                           </span>
                           <h3 className="font-bold text-white">{item.title}</h3>
                        </div>
                        <p className="text-sm text-blue-200/70">{item.description}</p>
                        {item.remediation && (
                          <div className="mt-2 text-xs text-blue-400 font-medium bg-blue-500/5 p-2 rounded">
                            <span className="font-black uppercase text-[9px] block mb-1">Resolution Pathway</span>
                            {item.remediation}
                          </div>
                        )}
                      </div>
                      <div className={`px-3 py-1 rounded text-[10px] font-black uppercase ${
                        item.status === 'pass' ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {item.status}
                      </div>
                   </div>
                </div>
              ))}
           </div>

           <div className="mt-8 pt-6 border-t border-blue-500/10 flex gap-4">
              <button className="flex-1 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-black text-xs uppercase tracking-widest transition-all">
                Download Official Report
              </button>
              <button 
                onClick={() => deleteAudit(record.id)}
                className="px-6 py-3 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-xl border border-red-500/20 font-black text-xs uppercase tracking-widest transition-all"
              >
                Archive Record
              </button>
           </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-20">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="space-y-2">
          <div className="flex items-center space-x-3">
            <Archive className="text-blue-500" size={32} />
            <h1 className="text-4xl font-black text-white tracking-tight">Audit <span className="text-blue-500">Repository</span></h1>
          </div>
          <p className="text-blue-200/60 text-lg">
            Historical record of all system assessments and compliance checkpoints.
          </p>
        </div>

        <div className="flex gap-4">
           <div className="bg-slate-900/50 p-4 rounded-2xl border border-blue-500/10 text-center min-w-[120px]">
              <p className="text-[10px] font-black text-blue-300/40 uppercase">Total Audits</p>
              <p className="text-2xl font-black text-white">{audits.length}</p>
           </div>
           <div className="bg-slate-900/50 p-4 rounded-2xl border border-blue-500/10 text-center min-w-[120px]">
              <p className="text-[10px] font-black text-blue-300/40 uppercase">Security Score</p>
              <p className="text-2xl font-black text-green-400">94%</p>
           </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-grow">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-300/50" size={20} />
          <input
            type="text"
            placeholder="Search repository by ID or type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-4 bg-slate-900/50 border border-blue-500/20 rounded-2xl text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-500/50 transition-all"
          />
        </div>

        <div className="flex gap-4">
          <div className="relative">
            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-300/50" size={18} />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-12 pr-10 py-4 bg-slate-900/50 border border-blue-500/20 rounded-2xl text-white appearance-none focus:outline-none focus:border-blue-500/50 transition-all font-bold text-sm uppercase tracking-widest cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="passed">Passed</option>
              <option value="failed">Failed</option>
              <option value="warning">Warning</option>
            </select>
          </div>
          <button 
            onClick={fetchAuditHistory}
            className="p-4 bg-blue-600/10 text-blue-400 border border-blue-500/20 rounded-2xl hover:bg-blue-600/20 transition-all"
          >
            <Activity size={24} />
          </button>
        </div>
      </div>

      {/* Audit List */}
      {loading ? (
        <div className="py-20 text-center space-y-4">
           <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin mx-auto"></div>
           <p className="text-blue-300/40 font-black uppercase tracking-[0.2em] text-xs">Accessing Encrypted Records...</p>
        </div>
      ) : filteredAudits.length === 0 ? (
        <div className="bg-slate-900/50 rounded-3xl border border-blue-500/10 p-20 text-center">
          <Archive size={64} className="text-blue-500/20 mx-auto mb-6" />
          <h2 className="text-2xl font-black text-white uppercase mb-2">No Records Found</h2>
          <p className="text-blue-300/40 max-w-sm mx-auto">The repository is currently empty. Run a system assessment to populate the history.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredAudits.map((audit) => (
            <div 
              key={audit.id} 
              onClick={() => fetchAuditDetails(audit.id)}
              className="group bg-slate-900/30 hover:bg-slate-800/50 rounded-2xl border border-blue-500/10 p-6 cursor-pointer transition-all hover:border-blue-500/40"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex items-center space-x-6">
                   <div className={`p-4 rounded-xl border ${
                     audit.status === 'passed' ? 'bg-green-500/10 border-green-500/20 text-green-400' : 
                     audit.status === 'failed' ? 'bg-red-500/10 border-red-400/20 text-red-400' :
                     'bg-yellow-500/10 border-yellow-400/20 text-yellow-400'
                   }`}>
                      <Shield size={24} />
                   </div>
                   <div>
                      <h3 className="text-xl font-black text-white group-hover:text-blue-400 transition-colors uppercase tracking-tight">
                        {audit.audit_type} Assessment
                      </h3>
                      <div className="flex items-center space-x-4 mt-1">
                        <span className="flex items-center space-x-1 text-xs text-blue-300/40 font-bold uppercase">
                          <Calendar size={12} />
                          <span>{new Date(audit.started_at).toLocaleDateString()}</span>
                        </span>
                        <span className="text-[10px] font-mono text-blue-500/40 uppercase tracking-tighter">
                          ID: {audit.id.split('-')[0]}...
                        </span>
                      </div>
                   </div>
                </div>

                <div className="flex items-center justify-between md:justify-end gap-10">
                   <div className="hidden lg:grid grid-cols-3 gap-6">
                      <div className="text-center">
                        <p className="text-[9px] font-black text-blue-300/30 uppercase mb-1">Findings</p>
                        <p className="text-white font-bold">{audit.total_findings}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[9px] font-black text-blue-300/30 uppercase mb-1">Critical</p>
                        <p className="text-red-400 font-bold">{audit.critical_issues}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[9px] font-black text-blue-300/30 uppercase mb-1">Status</p>
                        <p className={`text-[10px] font-black uppercase ${
                          audit.status === 'passed' ? 'text-green-400' : 'text-red-400'
                        }`}>{audit.status}</p>
                      </div>
                   </div>
                   <div className="w-12 h-12 rounded-full border border-blue-500/10 flex items-center justify-center text-blue-400 group-hover:bg-blue-600 group-hover:text-white transition-all shadow-lg">
                      <ArrowRight size={20} />
                   </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer Summary */}
      <div className="flex items-center justify-center space-x-2 text-blue-300/20 font-black uppercase tracking-[0.3em] text-[10px] py-10">
         <Shield size={14} />
         <span>Immutable Audit Ledger v1.0.4</span>
      </div>
    </div>
  )
}
